<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { isEmpty, isEqual } from "lodash";
import { useMainStore } from "@/stores/mainStore";
import upperFirst from "@/components/methods/upperFirst.js";
import { validateActivityName } from "@/components/methods/index.js";
import ButtonComponent from "@/components/Button/ButtonComponent.vue";
import ServiceLocation from "@/components/Services/ServiceLocation.vue";
import Modal from "@/components/Modal";
import ServiceDetails from "@/components/ServiceDetails.vue";
import Availability from "@/modules/registration/components/service/Availability.vue";
import ServicePricing from "@/modules/registration/components/service/ServicePricing.vue";
import AgesSelect from "@/components/AgesSelect.vue";
import SelectInput from "@/components/Select/SelectInput.vue";
import { post } from "@/libs/axios.js";
import { getAgesString } from "@/libs/ages.helper";

const props = defineProps({
    activity: {
        type: Object,
        default: () => {
        },
    },
    venue: {
        type: Object,
        default: () => {
        },
    },
});

const mainStore = useMainStore();
const router = useRouter();

const showDeleteService = ref(false);
const showEditDetails = ref(false);
const editedServiceSpinner = ref(false);

const showEditStatus = mainStore.isAdmin;
// show delete status only for Sophia user
const { showDeleteStatus } = mainStore;

const getStatuses = computed(() => {
    const statuses = [
        "Published",
        "In Review",
    ];

    if (showDeleteStatus) {
        statuses.push("Delete");
    }
    return statuses;
});

let editedService = ref({});

const serviceDetailsRef = ref(null);

const defaultVenueData = ref({});
const defaultVenueMobile = ref({});

const serviceLocation = ref(null);
const showEditLocation = ref(false);
const fullVenueInfo = ref({});

const showEditAvailability = ref();

const editedAvailability = ref({});
const availabilityErrors = ref({});

const availabilitySpinner = ref(false);

const showEditPricing = ref(false);
const pricingSpinner = ref(false);

let editedPricing = ref({});

const servicePricingRef = ref(null);
const showEditAges = ref(false);
const serviceAges = ref([]);
const serviceAgesError = ref("");
const showEditAgesSpinner = ref(false);

const ages = computed(() => {
    if (!mainStore.initialDataRetrieved) {
        return "";
    }

    const actAges = mainStore?.agesForActivities[props.activity?.activity_id];

    return getAgesString(actAges);
});

const getInitAges = computed(() => {
    if (!mainStore.initialDataRetrieved) {
        return "";
    }

    const actAges = mainStore?.agesForActivities[props.activity?.activity_id];
    return actAges.flat().map((age) => ({ ...age, selected: true, id: parseInt(age.id, 10) }));
});

async function submitEditAges() {
    if (serviceAges.value.length === 0) {
        serviceAgesError.value = "Please select a value";
    } else {
        showEditAgesSpinner.value = true;
        const res = await mainStore.updateServiceAges(
            serviceAges.value,
            props.activity.activity_id,
        );
        showEditAgesSpinner.value = false;
        showEditAges.value = false;
        mainStore.getData();
    }
}

const categories = computed(() => {
    if (!mainStore.initialDataRetrieved) {
        return "";
    }
    const cat = mainStore.actCategories?.filter(
        (ac) => ac.activity_id === props.activity?.activity_id,
    );
    const filteredMainCategory = cat?.find((c) => c.category_type === "0");
    const mainCategory = mainStore.categories.find(
        (c) => c.category_id === filteredMainCategory?.category_id,
    );

    const filteredSecondaryCategory = cat.find((c) => c.category_type !== "0");
    const secondaryCategory = mainStore.categories.find(
        (c) => c.category_id === filteredSecondaryCategory?.category_id,
    );

    return {
        main: mainCategory?.name,
        secondary: secondaryCategory?.name ?? "/",
    };
});

const sessions = computed(() => {
    const filteredSessions = mainStore.sessions.filter(
        (s) => s.activity_id === props.activity?.activity_id
            && s.venue_id === props.venue?.venue_id,
    );

    return filteredSessions;
});

const getVenueInfoStatus = computed(() => {
    if (props.venue?.service_type === "area-location") {
        return "Mobile service and venue";
    }

    if (props.venue?.service_type === "area") {
        return "My service is mobile";
    }

    if (props.venue?.service_type === "location") {
        return "My service has a venue";
    }

    return "";
});

const prices = computed(() => {
    const price = mainStore.servicePricing.filter(
        (sp) => sp.venue_id === props.venue?.venue_id
            && sp.activity_id === props.activity?.activity_id,
    );
    return price;
});

function opedDeleteService() {
    showDeleteService.value = true;
}

async function confirmRemoveService() {
    showDeleteService.value = false;
    const res = await mainStore.removeService(props.venue.venue_id, props.activity.activity_id);

    if (res.data.success) {
        await mainStore.getData();
        router.push({ name: "Services" });
    }
}

function returnDefaultVenueData() {
    const ven = props?.venue;

    const venueData = {
        venueName: ven.name,
        address1: ven.address,
        address2: "",
        area: ven.county,
        city: ven.city,
        postcode: ven.postcode,
        lat: ven.geo_latitude,
        lng: ven.geo_longitude,
        country: "United Kingdom",
        autocomplete: {
            address: ven.address,
            area: ven.county,
            city: ven.city,
            postcode: ven.postcode,
        },
    };
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    defaultVenueData.value = venueData;

    const autocomplete = {};

    // eslint-disable-next-line no-return-assign
    ven.area_autocomplete?.split(", ").map((el) => (autocomplete[el] = el));

    const venueMobile = {
        venueName: "",
        address1: "",
        address2: "",
        area: "",
        city: "",
        postcode: ven.area_postcode,
        travelDistance: `${ven.radius} miles`,
        lat: ven.geo_latitude,
        lng: ven.geo_longitude,
        country: "United Kingdom",
        autocomplete: { ...autocomplete, postal_code: ven.area_postcode },
    };
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    defaultVenueMobile.value = venueMobile;

    const isLocation = ven?.service_type === "location" || ven?.service_type === "area-location";

    const isMobile = ven?.service_type === "area" || ven?.service_type === "area-location";

    const settings = {
        showLocationForm: isLocation,
        showMobileForm: isMobile,
        advancedForm: false,
        showSecondPart: false,
        mobileServiceSameVenue:
            ven?.area_postcode !== "" && ven?.area_postcode === ven?.postcode ? "yes" : "no",
        editFirstVenue: false,
        hasLocation: isLocation,
        isMobile,
    };

    return {
        venueData,
        venueMobile,
        settings,
    };
}

const getDefaultVenueLocation = computed(() => returnDefaultVenueData());

function updateVenueData(data) {
    fullVenueInfo.value = data;
}

async function updateLocation() {
    const { venueData, venueMobile, settings } = returnDefaultVenueData();
    const vData = { ...venueData, lat: parseFloat(venueData.lat), lng: parseFloat(venueData.lng) };
    const vMobileData = {
        ...venueMobile,
        lat: parseFloat(venueMobile.lat),
        lng: parseFloat(venueMobile.lng),
    };
    const isSameVenueData = isEqual({ ...fullVenueInfo.value.venueData }, vData);
    const isSameVenueMobile = isEqual({ ...fullVenueInfo.value.venueMobile }, vMobileData);
    const isSameSettings = isEqual(
        { ...fullVenueInfo.value.settings },
        { ...settings, enterManually: false },
    );

    if (!isSameVenueMobile || !isSameVenueData || !isSameSettings) {
        await mainStore.updateServiceVenue(props.venue.venue_id, fullVenueInfo.value);
        await mainStore.getData();
    }

    showEditLocation.value = false;
}

function submitEditLocationModal() {
    // Submit venue
    if (
        serviceLocation.value.hasLocation
        && !serviceLocation.value.isMobile
        && serviceLocation.value.validateLocation()
        && serviceLocation.value.enterManually === true
    ) {
        serviceLocation.value.mobileServiceSameVenue = "no";
        serviceLocation.value.store();
        updateLocation();
        return;
    }

    // Submit location
    if (
        !serviceLocation.value.hasLocation
        && serviceLocation.value.isMobile
        && serviceLocation.value.validateMobile()
    ) {
        serviceLocation.value.mobileServiceSameVenue = "no";
        serviceLocation.value.store();
        updateLocation();
        return;
    }

    // Submit if all checkboxes selected
    if (
        serviceLocation.value.hasLocation
        && serviceLocation.value.isMobile
        && serviceLocation.value.validateLocation()
        && serviceLocation.value.validateMobile()
        && serviceLocation.value.showSecondPart
        && !serviceLocation.value.editFirstVenue
    ) {
        serviceLocation.value.store();
        updateLocation();
        return;
    }

    // If both checkbox selected
    if (serviceLocation.value.advancedForm) {
        if (!serviceLocation.value.enterManually && !serviceLocation.value.showSecondPart) {
            serviceLocation.value.switchManualAddress();
            return;
        }
        if (serviceLocation.value.showLocationForm && serviceLocation.value.validateLocation()) {
            serviceLocation.value.showSecondPartForm();
            return;
        }
        if (serviceLocation.value.editFirstVenue) {
            serviceLocation.value.editFirstVenue = false;
            serviceLocation.value.enterManually = false;
            return;
        }
    }

    if (
        serviceLocation.value.hasLocation
        && !serviceLocation.value.isMobile
        && !isEmpty(serviceLocation.value)
        && serviceLocation.value.enterManually === false
    ) {
        serviceLocation.value.mobileServiceSameVenue = "no";
        serviceLocation.value.store();
        serviceLocation.value.switchManualAddress();
        return;
    }

    if (!serviceLocation.value.validateLocation()) {
        serviceLocation.value.showErrors();
    }
}

const getSubmitText = computed(() => {
    if (serviceLocation.value === null) {
        return "";
    }

    const serviceBoth = serviceLocation.value.hasLocation
        && serviceLocation.value.isMobile
        && serviceLocation.value.showSecondPart;

    const serviceVenue = serviceLocation.value.hasLocation
        && !serviceLocation.value.isMobile
        && serviceLocation.value.enterManually === true;

    const serviceMobile = !serviceLocation.value.hasLocation
        && serviceLocation.value.isMobile
        && serviceLocation.value.venueMobile.autocomplete !== null
        && serviceLocation.value.venueMobile.autocomplete !== undefined;

    if (serviceBoth || serviceVenue || serviceMobile) {
        return "Submit";
    }

    return "Next";
});

function edit(editType) {
    if (editType === "details") {
        showEditDetails.value = true;
    }
    if (editType === "availability") {
        showEditAvailability.value = true;
    }

    if (editType === "pricing") {
        showEditPricing.value = true;
    }
    if (editType === "ages") {
        showEditAges.value = true;
    }
}

async function submitEditDetails() {
    if (serviceDetailsRef.value.validate() === false) {
        return;
    }

    // eslint-disable-next-line vue/no-ref-as-operand
    if (props.activity.title !== editedService.name) {
        // check if user changing service name (if not, dont check as its their activity/service name which is ok)
        // eslint-disable-next-line vue/no-ref-as-operand
        const response = await validateActivityName(editedService.name);

        if (response.data === "false" && response.errors.name) {
            serviceDetailsRef.value.errors.name = response.errors.name;
            return;
        }
    }

    editedServiceSpinner.value = true;
    const result = await post("/management-panel/service/update-details", {
        details: editedService,
    });
    if (result.data.success) {
        mainStore.getData();
        editedServiceSpinner.value = false;
        showEditDetails.value = false;
    } else {
        alert("something went wrong");
    }
}

const IMAGE_BASE_LINK = `${window.imgUrl}${window.businessId}/`;

function getBaseImageLink() {
    return IMAGE_BASE_LINK;
}

function getServiceDetails() {
    let secondaryCategoriesArray = [];
    if (categories.value.secondary.length > 0) {
        // eslint-disable-next-line max-len
        const secondaryCat = mainStore.allCategories.find(
            (c) => c.name === categories.value?.secondary,
        );
        secondaryCategoriesArray = [secondaryCat];
    }

    const service = {
        id: props.activity.activity_id,
        name: props.activity.title,
        description: props.activity.description,
        primaryCategory: categories.value?.main,
        secondaryCategories: secondaryCategoriesArray,
        bookingPolicy: props.activity?.booking_policy,
        bookingEmail: props.activity?.booking_email,
        bookingUrl: props.activity?.booking_link,
        imgUrl: `${getBaseImageLink()}${props.activity.listing_image_name}`,
    };

    return service;
}

function validateAvailability() {
    const availability = editedAvailability.value?.availability;

    Object.keys(availability).map((el) => {
        if (availability[el].checked) {
            availabilityErrors.value[el] = {
                from: availability[el].from === "" ? "Please select from time" : "",
                to: availability[el].to === "" ? "Please select to time" : "",
            };
        }
    });

    const errors = Object.keys(availabilityErrors.value).filter((el) => {
        if (availabilityErrors.value[el].from !== "" || availabilityErrors.value[el].to !== "") {
            return "1";
        }
    });

    return errors.length === 0;
}

function listenToFormServiceChanges(service) {
    // just keep my copy of service up to date, so when user wants to save I know what to save
    editedService = service;
}

async function saveAvailability() {
    const valid = validateAvailability();

    if (!valid) {
        return;
    }

    availabilitySpinner.value = true;
    const result = await post("/management-panel/service/update-availability", {
        details: editedAvailability.value,
    });

    if (result.data.success) {
        mainStore.getData();
        showEditAvailability.value = false;
    } else {
        alert("something went wrong");
    }

    availabilitySpinner.value = false;
}

async function savePricing() {
    pricingSpinner.value = true;

    const valid = servicePricingRef.value.validate();
    if (valid === false) {
        pricingSpinner.value = false;
        return;
    }

    const result = await post("/management-panel/service/update-pricing", { details: editedPricing });

    if (result.data.success) {
        mainStore.getData();
        showEditPricing.value = false;
    } else {
        alert("something went wrong");
    }

    pricingSpinner.value = false;
}

function loadServiceProvidersAvailability() {
    const savedAvailDays = sessions.value.map((o) => {
        const obj = {
            weekday: o.day.charAt(0).toUpperCase() + o.day.slice(1),
            checked: true,
            from: o.time_start,
            to: o.time_finish,
        };
        return obj;
    });

    const alldays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const availabilityArrObjs = alldays.map((day) => {
        const theDayObj = savedAvailDays.filter((availDay) => availDay.weekday === day);
        if (theDayObj.length === 1) {
            return theDayObj[0];
        }
        return {
            weekday: day,
            checked: false,
            from: "",
            to: "",
        };
    });

    return availabilityArrObjs;
}

function getServiceAvailability() {
    const availabilityList = loadServiceProvidersAvailability();

    return {
        id: props.activity.activity_id,
        availability: availabilityList,
        venue_id: props.venue?.venue_id,
        title: props.activity,
    };
}

function listenForAvailabilityChange(changedAvailabilityInChild) {
    editedAvailability.value = changedAvailabilityInChild;
}

function getServicePricing() {
    const pricesList = mainStore.servicePricing
        .filter((sp) => sp.venue_id === props.venue?.venue_id
            && sp.activity_id === props.activity?.activity_id);

    const pricesList2 = pricesList.map((i) => ({ ...i, type: i.price_type })); // add a type field ( that child expects )

    return {
        id: props.activity.activity_id,
        venue_id: props.venue?.venue_id,
        // eslint-disable-next-line no-unneeded-ternary
        freeService: props.activity?.is_free === "1" ? true : false, // much clearer than what eslint suggesting
        prices: pricesList2,
    };
}

function listenForPricingChange(changedPricing) {
    editedPricing = changedPricing;
}

const isServiceFree = computed(() => {
    if (sessions.value !== undefined) {
        return sessions.value[0]?.is_free === "1";
    }
    return false;
});

function getStatusText(status) {
    switch (status) {
    case "submit":
        return "In Review";
    case "publish":
        return "Published";
    case "draft":
        return "In Draft";
    case "delete":
        return "Delete";
    default:
        return "In Draft";
    }
}

async function updateStatus(status) {
    if (status !== "Delete") {
        const res = await mainStore.updateStatus(props.activity?.activity_id, status);

        if (res.data.success) {
            await mainStore.getData();
        }
    } else {
        showAdminConfirmDeleteActivity.value = true;
    }
}

</script>

<template>
    <div class="d-flex flex-row">
        <div class="w-100">
            <div class="mt-5 d-flex flex-row">
                <div class="align-self-center mr-2">
                    <img
                        class="medium-img"
                        :src="mainStore.IMAGE_BASE_LINK + props.activity?.listing_image_name"
                    />
                </div>
                <div class="d-flex flex-column">
                    <h5 class="act-title">{{ props.activity?.title }}</h5>
                    <!-- TODO show info depending on venue type for service -->
                    <div class="info">{{ getVenueInfoStatus }}</div>
                </div>
            </div>
        </div>
        <div v-if="showEditStatus" class="status-block mt-5">
            <SelectInput
                :options="getStatuses"
                :default="getStatusText(props.activity?.status)"
                @change="updateStatus"
                placeholder="Choose an event type"
            />
        </div>
    </div>

    <div>
        <div class="d-flex flex-column summary-block">
            <div class="d-flex flex-row justify-content-between">
                <div class="simple-title">Location</div>
                <div
                    @click="showEditLocation = !showEditLocation"
                    class="edit-button cursor-pointer"
                >
                    Edit
                </div>
            </div>

            <div v-if="props.venue?.service_type === 'area'">
                <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                    <div>Servicing area</div>
                    <div class="event-data">{{ props.venue?.area_postcode }}</div>
                </div>

                <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                    <div>Travel distance</div>
                    <div class="event-data">+ {{ props.venue.radius }} miles</div>
                </div>
            </div>
            <div v-if="props.venue?.service_type === 'area-location'">
                <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                    <div>Venue address</div>
                    <div class="event-data">
                        {{ props.venue.address }}, {{ props.venue.city }} ,
                        {{ props.venue.postcode }}
                    </div>
                </div>

                <div
                    v-if="props.venue?.postcode !== props.venue?.area_postcode"
                    class="d-flex flex-row justify-content-between mt-3 pl-3"
                >
                    <div>Servicing area</div>
                    <div class="event-data">{{ props.venue?.area_postcode }}</div>
                </div>

                <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                    <div>Travel distance</div>
                    <div class="event-data">+ {{ props.venue.radius }} miles</div>
                </div>
            </div>

            <div v-if="props.venue?.service_type === 'location'">
                <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                    <div>Venue address</div>
                    <div class="event-data">
                        {{ props.venue.address }}, {{ props.venue.city }} ,
                        {{ props.venue.postcode }}
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex flex-column summary-block">
            <div class="d-flex flex-row justify-content-between">
                <div class="simple-title">Ages</div>
                <div @click="edit('ages')" class="edit-button cursor-pointer">Edit</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Suitable for</div>
                <div class="event-data">{{ ages }}</div>
            </div>
        </div>

        <div class="d-flex flex-column summary-block">
            <div class="d-flex flex-row justify-content-between">
                <div class="simple-title">Details</div>
                <div @click="edit('details')" class="edit-button cursor-pointer">Edit</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Name</div>
                <div class="event-data">{{ props.activity?.title }}</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Description</div>
                <div class="event-data">
                    {{ props.activity?.description?.slice(0, 20) }}
                    {{ props.activity?.description?.length > 20 ? "..." : "" }}
                </div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Primary category</div>
                <div class="event-data">{{ categories.main }}</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Secondary category</div>
                <div class="event-data">{{ categories?.secondary }}</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Booking policy</div>
                <div class="event-data">
                    {{ props.activity?.booking_policy?.slice(0, 20) }}
                    {{ props.activity?.booking_policy?.length > 20 ? "..." : "" }}
                </div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Email</div>
                <div class="event-data">{{ props.activity?.booking_email }}</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>URL</div>
                <div class="event-data">{{ props.activity?.booking_url }}</div>
            </div>
        </div>

        <div class="d-flex flex-column summary-block">
            <div class="d-flex flex-row justify-content-between">
                <div class="simple-title">Availability</div>
                <div @click="edit('availability')" class="edit-button cursor-pointer">Edit</div>
            </div>

            <div
                v-if="sessions.length"
                v-for="session in sessions"
                :key="session.timetable_id"
                class="d-flex flex-row justify-content-between mt-3 pl-3"
            >
                <div>{{ upperFirst(session.day) }}</div>
                <div class="event-data">{{ session.time_start }} to {{ session.time_finish }}</div>
            </div>
            <div v-else class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>No availability details added</div>
            </div>
        </div>

        <div class="d-flex flex-column summary-block">
            <div class="d-flex flex-row justify-content-between">
                <div class="simple-title">Price</div>
                <div @click="edit('pricing')" class="edit-button cursor-pointer">Edit</div>
            </div>

            <div v-if="sessions?.length > 0" class="d-flex flex-row justify-content-between mt-3 pl-3">
                <div>Free service</div>
                <div class="event-data">
                    {{ isServiceFree ? "Yes" : "No" }}
                </div>
            </div>

            <div
                v-if="!isServiceFree"
            >
                <div
                    v-for="(price, idx) in prices"
                    :key="idx"
                    class="d-flex flex-row justify-content-between mt-3 pl-3"
                >
                    <div>{{ price.ticket_type }}</div>
                    <div class="event-data">&#163; {{ price.price }}</div>
                </div>
            </div>
        </div>
    </div>

    <div class="mt-5">
        <ButtonComponent
            @click="opedDeleteService"
            type="warning"
        >
            Delete service
        </ButtonComponent>
    </div>

    <Modal
        v-if="showDeleteService"
        :show-modal="showDeleteService"
        :title="'<h5>Remove service</h5>'"
        :remove-modal="true"
        close-text="No"
        submit-text="Yes"
        @submit-modal="confirmRemoveService"
        @close-modal="showDeleteService = false"
        modal-dialog-class="modal-md"
    >
        <div class="mt-4">
            Are you sure you want to delete service ?
        </div>
    </Modal>

    <Modal
        v-if="showEditDetails"
        :show-modal="showEditDetails"
        :show-spinner="editedServiceSpinner"
        @close-modal="showEditDetails = false"
        @submit-modal="submitEditDetails"
    >
        <ServiceDetails
            ref="serviceDetailsRef"
            :on-edit-summary-page="true"
            :service-details="getServiceDetails()"
            @update="listenToFormServiceChanges"
        />
    </Modal>
    <Modal
        v-if="showEditAvailability"
        :show-modal="showEditAvailability"
        :show-spinner="availabilitySpinner"
        @submit-modal="saveAvailability"
        @close-modal="showEditAvailability = false"
    >

        <Availability
            :from-edit-service=true
            :service-availability="getServiceAvailability()"
            :errors="availabilityErrors"
            @updateAvailability="listenForAvailabilityChange"
        />
    </Modal>

    <Modal
        v-if="showEditLocation"
        :show-modal="showEditLocation"
        :title="'<h5>Edit location</h5>'"
        :submit-text="getSubmitText"
        @submit-modal="submitEditLocationModal"
        @close-modal="showEditLocation = false"
    >
        <div>
            <ServiceLocation
                ref="serviceLocation"
                :default="getDefaultVenueLocation"
                @update="updateVenueData"
            />
        </div>
    </Modal>

    <Modal
        v-if="showEditAges"
        :show-modal="showEditAges"
        :title="'<h5>Edit ages</h5>'"
        :show-spinner="showEditAgesSpinner"
        @submit-modal="submitEditAges"
        @close-modal="showEditAges = false"
    >
        <div class="mt-3">
            <AgesSelect
                :hide-label="true"
                :default="getInitAges"
                @update="(e) => (serviceAges = e)"
                :error="serviceAgesError"
            />
        </div>
    </Modal>

    <Modal
        v-if="showEditPricing"
        :show-modal="showEditPricing"
        :show-spinner="pricingSpinner"
        @submit-modal="savePricing"
        @close-modal="showEditPricing = false"
    >
        <ServicePricing ref="servicePricingRef"
                        :from-edit-pricing=true
                        :service-pricing="getServicePricing()"
                        :sessions-added="sessions.length > 0"
                        :is-free="isServiceFree"
                        @updatePricing="listenForPricingChange"
        />

    </Modal>
</template>

<style scoped>
h1 {
    color: #272529;
    font-style: normal;
    font-weight: 600;
    font-size: 2rem;
    line-height: 48px;
}

.info {
    font-style: normal;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 1.315rem;
    color: #636066;
}

.simple-title {
    font-style: normal;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.315rem;
    color: #272529;
    margin-top: 46px;
    margin-bottom: 16px;
}

.event-data {
    font-weight: 400;
    color: #636066;
}

.edit-button {
    font-style: normal;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.315rem;
    color: #260045;
    margin-top: 46px;
}

.status-block {
    min-width: 150px;
}
</style>
